/**
 * Compatibility Home — lists the current user's Y-site compatibility sessions
 * and lets them open a new one. Served at the root of the compatibility
 * subdomain. Uses the same api-service-go backend + `compatibility` agent as
 * everything else (the "usual interface", like Care Planner).
 *
 * GET /sessions lists every session the caller may read (no agent filter on
 * the route), so the list filters to this agent client-side. "New check" opens
 * the session-less matrix (/sessions/new): the matrix is a build-time snapshot,
 * so a check needs no session and creates none.
 */

import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, ChevronRight } from 'lucide-react'
import { useSessions } from '@frontend/hooks/useSessions'
import { Skeleton } from '@frontend/components/ui/skeleton'
import { Button } from '@frontend/components/ui/button'
import { ReturnToMainAgent } from '@frontend/components/ReturnToMainAgent'
import { MainAppNav } from '@frontend/components/MainAppNav'
import { AGENT_SLUG } from '../agent'

export default function CompatibilityHome() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: allSessions, isLoading } = useSessions()
  const sessions = (allSessions ?? []).filter((s) => s.agent?.slug === AGENT_SLUG)

  const handleCreate = () => {
    navigate('/sessions/new')
  }

  return (
    <div className="flex flex-col h-dvh bg-neutral-50">
      <header className="flex items-center gap-2 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] border-b border-neutral-200 bg-white">
        <ReturnToMainAgent className="-ml-2" />
        <h1 className="min-w-0 flex-1 truncate text-base font-semibold text-neutral-900">
          {t('compatibility.homeTitle', 'Y-site compatibility')}
        </h1>
        <MainAppNav />
        <Button onClick={handleCreate} size="sm" className="gap-1">
          <Plus className="w-4 h-4" />
          {t('compatibility.newCheck', 'New check')}
        </Button>
      </header>

      <main className="flex-1 overflow-auto px-4 py-3">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <p className="text-lg text-neutral-600 mb-2">
              {t('compatibility.emptyHomeTitle', 'No checks yet')}
            </p>
            <p className="text-sm text-neutral-400 mb-4">
              {t('compatibility.emptyHomeDescription', 'Open the Y-site matrix to check whether two drugs can be co-infused')}
            </p>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-1" />
              {t('compatibility.newCheck', 'New check')}
            </Button>
          </div>
        ) : (
          <ul className="space-y-2">
            {sessions.map((session) => (
              <li key={session.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/sessions/${session.id}`)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-neutral-200 transition hover:bg-neutral-50 active:scale-[0.98] active:bg-neutral-50"
                >
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-semibold text-neutral-900 truncate">
                      {session.title || t('compatibility.untitled', 'Untitled check')}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {new Date(session.updated_at).toLocaleString()}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  )
}
